import { makeVar, useReactiveVar } from "@apollo/client";
import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { pendingChatPromptVar } from "@apps/ask-ai/ask-ai-context";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";

import {
  importRoleFrameworkPrompt,
  roleFrameworkPrompt,
  rolesAndCompetenciesAskAiTarget,
} from "./role-framework-helpers";

export const rolesAskAiSidebarPreferenceKey =
  "rolesAndCompetenciesAskAiSidebarExpanded";

// Whether "Build with AI" already started the role framework chat in the
// sidebar during this visit, so later clicks only show/hide the sidebar.
const roleFrameworkChatStartedVar = makeVar(false);

// Counts the changes the sidebar's chat confirmed, so a page that doesn't
// fetch what it shows (a track the editor just created) knows to fetch it.
export const roleFrameworkChangeCountVar = makeVar(0);

// The chat lives in the sidebar: once the sidebar is gone, so is the chat, and
// the next "Build with AI" has to start it again.
export const resetRoleFrameworkChatStarted = () =>
  roleFrameworkChatStartedVar(false);

// Ask AI sidebar shared by the Roles and Competencies, career track and
// competency pages, so the framework can be edited with AI next to the page.
const useRolesAskAiSidebar = () => {
  const label = useLabel();
  const organization = useReactiveVar(currentOrganizationVar);
  // Global and people admins: the only ones Ask AI offers the role framework
  // tools to (backend `offers_role_framework`).
  const isAdmin = useReactiveVar(isAdminVar);
  const { organizationId } = useParams<{ organizationId?: string }>();
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const chatStarted = useReactiveVar(roleFrameworkChatStartedVar);
  // Ask AI works in the current organization, so it's only offered on that
  // organization's settings, and only when the organization has Ask AI.
  // Career Tracks is open to everyone, but only admins can change it.
  const isAvailable =
    !!organization.featureFlags.askAi &&
    isAdmin &&
    (!organizationId || parseInt(organizationId) === organization.id);
  const expanded =
    isAvailable && !!uiPreferenceCache[rolesAskAiSidebarPreferenceKey];

  // Opens the sidebar and sends `prompt` to the chat there.
  const startChat = useCallback(
    (prompt: string) => {
      roleFrameworkChatStartedVar(true);
      pendingChatPromptVar({ target: rolesAndCompetenciesAskAiTarget, prompt });
      saveUiPreference({ [rolesAskAiSidebarPreferenceKey]: true });
    },
    [saveUiPreference],
  );

  // "Build with AI": the first click opens the sidebar and starts the
  // build-role-framework skill there; after that it toggles the sidebar.
  const toggleBuildWithAi = useCallback(() => {
    if (expanded) {
      saveUiPreference({ [rolesAskAiSidebarPreferenceKey]: false });
    } else if (!chatStarted) {
      startChat(roleFrameworkPrompt(label("competency")));
    } else {
      saveUiPreference({ [rolesAskAiSidebarPreferenceKey]: true });
    }
  }, [chatStarted, expanded, label, saveUiPreference, startChat]);

  // "Import": starts the import skill, even over a chat already under way.
  const startImport = useCallback(
    () => startChat(importRoleFrameworkPrompt(label("competency"))),
    [label, startChat],
  );

  return {
    isAvailable,
    expanded,
    toggleBuildWithAi,
    startImport,
    // Page content makes room for the fixed sidebar (same widths as Layout).
    contentClassName: expanded ? "lg:mr-96 xl:mr-120 2xl:mr-144" : "",
  };
};

export default useRolesAskAiSidebar;
